import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Table, TBody, Td, THead, Th, Tr } from "../../../components/ui/table";
import { api, qk, unwrap } from "../../../lib/api";
import { authClient } from "../../../lib/auth";
import { toastError, useMe } from "../../../lib/hooks";

export const Route = createFileRoute("/_app/admin/users")({ component: AdminUsers });

function AdminUsers() {
  const me = useMe();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const users = useQuery({
    queryKey: qk.admin.users(q),
    queryFn: () => unwrap(api.admin.users.$get({ query: { q } })),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const act = useMutation({
    mutationFn: async ({
      userId,
      op,
    }: {
      userId: string;
      op: "ban" | "unban" | "impersonate" | "reset";
    }) => {
      if (op === "reset")
        return unwrap(
          api.admin.users[":userId"]["send-password-reset"].$post({ param: { userId } }),
        );
      const r =
        op === "ban"
          ? await authClient.admin.banUser({
              userId,
              banReason: prompt("Reason (optional):") ?? undefined,
            })
          : op === "unban"
            ? await authClient.admin.unbanUser({ userId })
            : await authClient.admin.impersonateUser({ userId });
      if (r.error) throw new Error(r.error.message ?? "Failed");
    },
    onSuccess: async (_r, { op }) => {
      if (op === "impersonate") {
        qc.clear();
        window.location.href = "/";
        return;
      }
      if (op === "reset") toast.success("Password reset email sent");
      await refresh();
    },
    onError: toastError,
  });

  return (
    <div className="flex flex-col gap-4">
      <Input
        placeholder="Search name or email"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="max-w-sm"
      />
      <Table>
        <THead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Organizations</Th>
            <Th />
          </Tr>
        </THead>
        <TBody>
          {users.data?.users.map((u) => (
            <Tr key={u.id}>
              <Td className="font-medium">
                {u.name} {u.role === "admin" && <Badge>platform admin</Badge>}{" "}
                {u.banned && <Badge variant="destructive">banned</Badge>}
              </Td>
              <Td className="text-muted-foreground">{u.email}</Td>
              <Td className="text-muted-foreground">
                {u.orgs.map((o) => `${o.name} (${o.role})`).join(", ")}
              </Td>
              <Td className="space-x-1 text-right whitespace-nowrap">
                {u.id !== me.user.id && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act.mutate({ userId: u.id, op: "reset" })}
                    >
                      Reset password
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act.mutate({ userId: u.id, op: "impersonate" })}
                    >
                      Impersonate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act.mutate({ userId: u.id, op: u.banned ? "unban" : "ban" })}
                    >
                      {u.banned ? "Unban" : "Ban"}
                    </Button>
                  </>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
